import client from './client'
import type { User } from '@/types/auth'
import type { Locale } from '@/i18n'

export interface LoginPayload {
  email: string
  password: string
  remember: boolean
}

export interface RegisterPayload {
  first_name: string
  last_name: string
  email: string
  password: string
  password_confirmation: string
  phone: string
  organisation_name: string
  organisation_slug: string
  address_line_1: string
  address_line_2: string
  city: string
  postal_code: string
  country_code: string
  vat_number: string
  organisation_email: string
  organisation_phone: string
  website: string
  logo: File | null
  preferred_language: Locale
}

export interface ForgotPasswordPayload {
  email: string
}

export interface ResetPasswordPayload {
  token: string
  email: string
  password: string
  password_confirmation: string
}

export async function fetchCsrfCookie(): Promise<void> {
  // Strip /api/v1 from the base URL to get the Laravel root domain
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'
  const laravelBase = apiBase.replace(/\/api\/v1$/, '')
  await client.get('/sanctum/csrf-cookie', { baseURL: laravelBase })
}

export async function login(payload: LoginPayload): Promise<User> {
  const { data } = await client.post<{ data: User }>('/auth/organizer/login', payload)
  return data.data
}

export async function register(payload: RegisterPayload): Promise<User> {
  const formData = new FormData()
  formData.append('first_name', payload.first_name)
  formData.append('last_name', payload.last_name)
  formData.append('email', payload.email)
  formData.append('password', payload.password)
  formData.append('password_confirmation', payload.password_confirmation)
  formData.append('phone', payload.phone)
  formData.append('organisation_name', payload.organisation_name)
  formData.append('organisation_slug', payload.organisation_slug)
  formData.append('address_line_1', payload.address_line_1)
  if (payload.address_line_2) formData.append('address_line_2', payload.address_line_2)
  formData.append('city', payload.city)
  formData.append('postal_code', payload.postal_code)
  formData.append('country_code', payload.country_code)
  if (payload.vat_number) formData.append('vat_number', payload.vat_number)
  formData.append('organisation_email', payload.organisation_email)
  if (payload.organisation_phone) formData.append('organisation_phone', payload.organisation_phone)
  if (payload.website) formData.append('website', payload.website)
  if (payload.logo) formData.append('logo', payload.logo)
  formData.append('preferred_language', payload.preferred_language)

  const { data } = await client.post<{ data: User }>('/auth/organizer/register', formData)
  return data.data
}

export async function checkEmailAvailability(email: string): Promise<{ available: boolean }> {
  const { data } = await client.get<{ available: boolean }>('/auth/check-email', {
    params: { email },
  })
  return data
}

// Thin semantic wrapper around checkEmailAvailability for call sites that
// reason about "does an account already exist for this email" rather than
// "is this email free to register with" — same endpoint, inverted meaning.
export async function emailHasAccount(email: string): Promise<boolean> {
  const { available } = await checkEmailAvailability(email)
  return !available
}

export async function checkSlugAvailability(slug: string): Promise<{ available: boolean }> {
  const { data } = await client.get<{ available: boolean }>('/auth/check-slug', {
    params: { slug },
  })
  return data
}

export async function logout(): Promise<void> {
  await client.post('/auth/logout')
}

export async function fetchCurrentUser(): Promise<User> {
  const { data } = await client.get<{ data: User }>('/auth/user')
  return data.data
}

export async function resendVerificationEmail(): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/email/resend')
  return data
}

export async function forgotPassword(payload: ForgotPasswordPayload): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/password/forgot', payload)
  return data
}

export async function resetPassword(payload: ResetPasswordPayload): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/password/reset', payload)
  return data
}
